import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'

export interface AgentTeam {
  id: number
  title: string
  source: string
  source_ref: string | null
  owner_user_id: number
  created_at: string
  updated_at: string
}

interface AgentTeamListResponse {
  items: AgentTeam[]
  total: number
}

export function useAgentTeams() {
  const [teams, setTeams] = useState<AgentTeam[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchTeams = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await api.get<AgentTeamListResponse>('/agent-teams')
      setTeams(response.data.items ?? [])
      setTotal(response.data.total ?? 0)
    } catch (err) {
      console.error('Failed to fetch agent teams:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch teams'))
      setTeams([])
      setTotal(0)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTeams()
  }, [fetchTeams])

  // Create team - returns boolean for success
  const create = useCallback(async (title: string): Promise<boolean> => {
    try {
      await api.post<AgentTeam>('/agent-teams', { title })
      await fetchTeams()
      return true
    } catch (err) {
      console.error('Failed to create team:', err)
      return false
    }
  }, [fetchTeams])

  // Rename team - returns boolean for success
  const rename = useCallback(async (id: number, title: string): Promise<boolean> => {
    try {
      await api.patch<AgentTeam>(`/agent-teams/${id}`, { title })
      await fetchTeams()
      return true
    } catch (err) {
      console.error('Failed to rename team:', err)
      return false
    }
  }, [fetchTeams])

  // Remove team - returns boolean for success
  const remove = useCallback(async (id: number): Promise<boolean> => {
    try {
      await api.delete(`/agent-teams/${id}`)
      await fetchTeams()
      return true
    } catch (err) {
      console.error('Failed to delete team:', err)
      return false
    }
  }, [fetchTeams])

  return {
    teams,
    total,
    isLoading,
    error,
    refetch: fetchTeams,
    create,
    rename,
    remove,
  }
}
